import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDownloadURL, ref } from 'firebase/storage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  canAcceptCardForBooking,
  fetchEmployeeSchedule,
  fetchEmployees,
  getCurrentBusinessConfig,
} from '../../firebase/firebase-delegate';
import {fetchPublicEmployees, getHomepageRuntimeConfig} from '../../sitePublishing/publicConfigSource';
import { storage } from '../../firebase/firebase';
import { useTenant } from '../../tenancy/TenantContext';
import { filterSlotsByMinimumNoticeThreshold, findFreeSlots, isTimeFree, timeToMinutes } from '../../time/timeUtils';
import { Appointment, BookingConfirmation, Employee, ScheduleCacheUpdateInput, Service } from '../../types';
import { dayjs } from '../../utils/dayjs-config';
import { getCachedAssetUrl } from '../../utils/assetCache';
import { applyScheduleBitstringPatch, setScheduleOverrideMap } from '../../utils/cache-delegate';
import {
  CARD_ELIGIBILITY_STALE_TIME,
  EMPLOYEE_SCHEDULE_STALE_TIME,
  buildAnyAvailableSchedulePlaceholder,
  getEligibleEmployeesForServices,
  getEmployeeLabel,
  getServicesByIds,
  getUnionSlotsForSchedules,
  shouldAllowAnyAvailable,
} from './bookNowDialogUtils';
import { resolveBookingUxConfig } from '../../utils/bookingUxConfig';

export interface UseBookNowViewModelParams {
  appointment: Appointment | null;
  handleAppointment: (appointment: Appointment | null) => void;
  services: Service[];
  enabledPayments: boolean;
  requirePaymentForBooking: boolean;
  minimumBookingNoticeHours: number;
  variant: 'customer' | 'internal';
  allowedEmployeeIds?: string[];
  preferredEmployeeId?: string;
  onBookingSuccess?: (booking: BookingConfirmation) => void;
  clearAppointmentOnSuccess?: boolean;
  scheduleCacheOverride?: Map<string, string> | null;
  scheduleCacheOverrideEmployeeId?: string;
  initialStep?: number;
  showEmployeePickerOverride?: boolean;
  employeesOverride?: Employee[];
}

const fetchAndPreloadImages = async (employees: Employee[]) => {
  const newImages: Record<string, string> = {};
  const loadPromises: Promise<void>[] = [];

  await Promise.all(
    employees.map(async (employee) => {
      const picturePath = employee.profilePictureImagePath;
      if (!picturePath) {
        return;
      }

      try {
        const url = await getCachedAssetUrl(
          picturePath,
          () => getDownloadURL(ref(storage, picturePath))
        );

        if (!url) {
          return;
        }

        newImages[employee.id] = url;

        const loadPromise = new Promise<void>((resolve) => {
          const img = new Image();
          img.src = url;
          img.onload = () => resolve();
          img.onerror = () => resolve();
        });

        loadPromises.push(loadPromise);
      } catch (error) {
        console.error('Error fetching image for employee', employee.id, error);
      }
    })
  );

  await Promise.all(loadPromises);
  return newImages;
};

const DEFAULT_QUERY_STALE_TIME = 1000 * 60 * 5;

export const useBookNowViewModel = ({
  appointment,
  handleAppointment,
  services,
  enabledPayments,
  requirePaymentForBooking,
  minimumBookingNoticeHours,
  variant,
  allowedEmployeeIds,
  preferredEmployeeId,
  onBookingSuccess,
  clearAppointmentOnSuccess,
  scheduleCacheOverride,
  scheduleCacheOverrideEmployeeId,
  initialStep,
  showEmployeePickerOverride,
  employeesOverride,
}: UseBookNowViewModelParams) => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();

  const [selectedServicesList, setSelectedServicesList] = useState<Service[] | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [isAnyAvailableSelected, setIsAnyAvailableSelected] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [anyAvailableSchedules, setAnyAvailableSchedules] = useState<Record<string, Map<string, string>>>({});
  const [anyAvailableLoadFailed, setAnyAvailableLoadFailed] = useState(false);
  const [isLoadingAnyAvailableSchedules, setIsLoadingAnyAvailableSchedules] = useState(false);
  const [anyAvailableCardEligibility, setAnyAvailableCardEligibility] = useState<Record<string, boolean>>({});
  const [isLoadingAnyAvailableCardEligibility, setIsLoadingAnyAvailableCardEligibility] = useState(false);
  const [profilePictures, setProfilePictures] = useState<Record<string, string>>({});
  const [imagesLoaded, setImagesLoaded] = useState<boolean>(false);
  const [isSubmitting, setIsSubmittingState] = useState<boolean>(false);
  const setIsSubmitting = useCallback((value: boolean) => {
    setIsSubmittingState(value);
  }, []);

  const [bookingConfirmation, setBookingConfirmation] = useState<BookingConfirmation | null>(null);
  const [paid, setPaid] = useState<boolean>(false);
  const [attemptedCardPayment, setAttemptedCardPayment] = useState<boolean>(false);

  const [currentStep, setCurrentStep] = useState(() => {
    if (typeof initialStep !== 'number') return 0;
    return Math.max(0, Math.min(initialStep, 4));
  });
  const [hasManualStepOverride, setHasManualStepOverride] = useState(false);

  const steps = ['1', '2', '3', '4', '5'];

  const goToNextStep = useCallback(() => {
    setHasManualStepOverride(false);
    setCurrentStep((prev) => Math.min(steps.length - 1, prev + 1));
  }, [steps.length]);

  const businessConfigQuery = useQuery({
    queryKey: [variant === 'customer' ? 'publicBusinessConfig' : 'businessConfig', tenantId],
    queryFn: () => variant === 'customer' ?
      getHomepageRuntimeConfig(tenantId) :
      getCurrentBusinessConfig(tenantId),
    staleTime: DEFAULT_QUERY_STALE_TIME,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isOrchestratorMode =
    variant === 'customer' &&
    !!businessConfigQuery.data?.preferences?.orchestratorMode;

  const bookingUxConfig = useMemo(
    () => resolveBookingUxConfig(businessConfigQuery.data),
    [businessConfigQuery.data]
  );
  const isResourceMode = variant === 'customer' && bookingUxConfig.bookingModel === 'resource';
  const shouldHideEmployeePicker = variant === 'customer' && (
    isResourceMode || showEmployeePickerOverride === false ||
    (showEmployeePickerOverride === undefined && bookingUxConfig.uiFlags.showEmployeePickerInBooking === false)
  );

  const minimumSelectableStep = (isOrchestratorMode || shouldHideEmployeePicker) ? 1 : 0;

  const goToPreviousStep = useCallback(() => {
    setHasManualStepOverride(true);
    setCurrentStep((prev) => Math.max(minimumSelectableStep, prev - 1));
  }, [minimumSelectableStep]);

  const normalizedMinimumBookingNoticeHours = Number.isFinite(minimumBookingNoticeHours)
    ? Math.max(0, minimumBookingNoticeHours)
    : 1;

  const selectionSummary = useMemo(() => {
    const employeeLabel = isAnyAvailableSelected && currentStep < 4
      ? 'Any available'
      : getEmployeeLabel(selectedEmployee);

    const serviceTitles = selectedServicesList?.length
      ? selectedServicesList.map((service) => service.title).filter(Boolean).join(', ')
      : appointment?.servicesList?.length
        ? appointment.servicesList.map((service) => service.serviceTitle).filter(Boolean).join(', ')
        : '';

    return {
      employeeLabel,
      serviceTitles,
    };
  }, [appointment?.servicesList, currentStep, isAnyAvailableSelected, selectedEmployee, selectedServicesList]);

  const getEarliestBookingDateTime = useCallback(() => {
    if (normalizedMinimumBookingNoticeHours <= 0) return null;
    return dayjs.tz().add(normalizedMinimumBookingNoticeHours, 'hour');
  }, [normalizedMinimumBookingNoticeHours]);

  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleErrorDialogOpen = useCallback((message: string, options?: { refreshSchedule?: boolean }) => {
    setErrorMessage(message);
    setOpenErrorDialog(true);

    if (options?.refreshSchedule) {
      if (appointment) {
        handleAppointment({ ...appointment, appointmentTime: '' });
      }
      setAvailableSlots([]);
      setCurrentStep(2);
      setHasManualStepOverride(false);
      const employeeKey = appointment?.employeeID || selectedEmployee?.id;
      if (employeeKey) {
        queryClient.invalidateQueries({ queryKey: ['employeeSchedule', tenantId, employeeKey] });
      }
    }
  }, [
    appointment,
    handleAppointment,
    queryClient,
    selectedEmployee?.id,
    tenantId,
  ]);

  const handleErrorDialogClose = useCallback(() => {
    setOpenErrorDialog(false);
  }, []);
  const handleErrorDialogExited = useCallback(() => {
    setErrorMessage(null);
  }, []);

  const handleBookingConfirmation = useCallback((bookingDetails: BookingConfirmation, attempted: boolean, isPaid: boolean) => {
    setPaid(isPaid);
    setAttemptedCardPayment(attempted);
    setBookingConfirmation(bookingDetails);
    onBookingSuccess?.(bookingDetails);
  }, [onBookingSuccess]);

  const shouldClearAfterSuccess = clearAppointmentOnSuccess ?? (variant !== 'internal');

  const useEmployeesOverride = Array.isArray(employeesOverride);

  const employeesQuery = useQuery<Employee[]>({
    queryKey: [variant === 'customer' ? 'publicEmployees' : 'employees', tenantId],
    queryFn: () => variant === 'customer' ? fetchPublicEmployees(tenantId) : fetchEmployees(tenantId),
    enabled: !useEmployeesOverride,
    staleTime: DEFAULT_QUERY_STALE_TIME,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const employeesData = useMemo(
    () => (useEmployeesOverride ? (employeesOverride ?? []) : (employeesQuery.data ?? [])),
    [employeesOverride, employeesQuery.data, useEmployeesOverride]
  );

  const employeesLoading = useEmployeesOverride
    ? false
    : (employeesQuery.isLoading || employeesQuery.isFetching);

  const employeesError = useEmployeesOverride
    ? null
    : employeesQuery.error;

  const hasEmployeesError = useEmployeesOverride
    ? false
    : employeesQuery.isError;

  const availableEmployees = useMemo(() => {
    const list = employeesData;
    const allowedSet = allowedEmployeeIds?.length ? new Set(allowedEmployeeIds) : null;
    return list.filter((employee) => {
      if (allowedSet && !allowedSet.has(employee.id)) {
        return false;
      }

      if (variant !== 'customer') {
        return true;
      }
      if (isResourceMode && employee.isOwner === true) {
        return false;
      }

      const isActive = employee.active !== false;
      const isBookable = employee.bookable !== false;
      return isActive && isBookable;
    });
  }, [allowedEmployeeIds, employeesData, isResourceMode, variant]);

  const eligibleEmployees = useMemo(() => {
    return getEligibleEmployeesForServices(selectedServicesList ?? [], availableEmployees);
  }, [availableEmployees, selectedServicesList]);

  const requiresCardCompatibleEmployee =
    variant === 'customer' &&
    enabledPayments &&
    requirePaymentForBooking;

  const anyAvailableCandidateEmployees = useMemo(() => {
    if (!isAnyAvailableSelected) {
      return [];
    }

    return eligibleEmployees;
  }, [eligibleEmployees, isAnyAvailableSelected]);

  const ensureEmployeeSchedule = useCallback((employeeId: string) => {
    return queryClient.ensureQueryData<Map<string, string>>({
      queryKey: ['employeeSchedule', tenantId, employeeId],
      queryFn: () => fetchEmployeeSchedule(employeeId, tenantId),
      staleTime: EMPLOYEE_SCHEDULE_STALE_TIME,
    });
  }, [queryClient, tenantId]);

  const refreshAnyAvailableSchedules = useCallback(async () => {
    setIsLoadingAnyAvailableSchedules(true);

    try {
      const scheduleLoadEmployees = requiresCardCompatibleEmployee
        ? eligibleEmployees.filter((employee) => anyAvailableCardEligibility[employee.id])
        : eligibleEmployees;

      const scheduleEntries = await Promise.all(
        scheduleLoadEmployees.map(async (employee) => {
          const schedule = await queryClient.fetchQuery<Map<string, string>>({
            queryKey: ['employeeSchedule', tenantId, employee.id],
            queryFn: () => fetchEmployeeSchedule(employee.id, tenantId),
            staleTime: 0,
          });
          return [employee.id, schedule] as const;
        })
      );

      const nextSchedules = { ...anyAvailableSchedules };
      scheduleEntries.forEach(([employeeId, schedule]) => {
        nextSchedules[employeeId] = schedule;
      });
      setAnyAvailableSchedules(nextSchedules);

      return nextSchedules;
    } finally {
      setIsLoadingAnyAvailableSchedules(false);
    }
  }, [anyAvailableCardEligibility, anyAvailableSchedules, eligibleEmployees, queryClient, requiresCardCompatibleEmployee, tenantId]);

  const ensureCardEligibility = useCallback((employeeId: string) => {
    if (variant === 'customer') {
      return Promise.resolve(employeesData.find((employee) => employee.id === employeeId)?.canAcceptCard === true);
    }
    const hasBusinessStripeAccount = !!(
      businessConfigQuery.data?.payoutInfo?.useBusinessStripeAccount &&
      businessConfigQuery.data?.payoutInfo?.businessStripeAccountId
    );
    return queryClient.ensureQueryData<boolean>({
      queryKey: ['cardEligibility', tenantId, employeeId],
      queryFn: () => (
        hasBusinessStripeAccount
          ? Promise.resolve(true)
          : canAcceptCardForBooking(employeeId, tenantId, businessConfigQuery.data ?? null)
      ),
      staleTime: CARD_ELIGIBILITY_STALE_TIME,
    });
  }, [businessConfigQuery.data, employeesData, queryClient, tenantId, variant]);

  const canUseScheduleOverride =
    variant === 'internal' &&
    !!scheduleCacheOverride &&
    !!scheduleCacheOverrideEmployeeId &&
    selectedEmployee?.id === scheduleCacheOverrideEmployeeId;

  useEffect(() => {
    if (!canUseScheduleOverride || !scheduleCacheOverride || !scheduleCacheOverrideEmployeeId) return;

    setScheduleOverrideMap(
      queryClient,
      tenantId,
      scheduleCacheOverrideEmployeeId,
      scheduleCacheOverride
    );
  }, [canUseScheduleOverride, scheduleCacheOverride, scheduleCacheOverrideEmployeeId, queryClient, tenantId]);

  const scheduleQuery = useQuery<Map<string, string>>({
    queryKey: ['employeeSchedule', tenantId, selectedEmployee?.id],
    queryFn: () => fetchEmployeeSchedule(selectedEmployee!.id, tenantId),
    enabled: !!selectedEmployee?.id && !canUseScheduleOverride && !(isAnyAvailableSelected && currentStep < 4),
    initialData: canUseScheduleOverride && scheduleCacheOverride
      ? () => new Map(scheduleCacheOverride)
      : undefined,
    staleTime: EMPLOYEE_SCHEDULE_STALE_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const allowAnyAvailable = shouldAllowAnyAvailable(
    variant,
    preferredEmployeeId,
    businessConfigQuery.data?.preferences?.allowAnyAvailableEmployee ?? false,
  ) || isOrchestratorMode || shouldHideEmployeePicker;

  const shouldUseAnyAvailableFlow =
    allowAnyAvailable &&
    isAnyAvailableSelected &&
    !!selectedServicesList?.length;

  const cardEligibilityQuery = useQuery({
    queryKey: ['cardEligibility', tenantId, selectedEmployee?.id],
    enabled: !!selectedEmployee?.id && (!isAnyAvailableSelected || currentStep === 4),
    queryFn: () => variant === 'customer' ?
      Promise.resolve(selectedEmployee?.canAcceptCard === true) :
      canAcceptCardForBooking(selectedEmployee!.id, tenantId, businessConfigQuery.data ?? null),
  });

  const bookableEligibleEmployees = useMemo(() => {
    if (!requiresCardCompatibleEmployee) {
      return eligibleEmployees;
    }

    return eligibleEmployees.filter((employee) => anyAvailableCardEligibility[employee.id]);
  }, [anyAvailableCardEligibility, eligibleEmployees, requiresCardCompatibleEmployee]);


  useEffect(() => {
    if (scheduleQuery.isError || hasEmployeesError) {
      const errorText =
        employeesError?.message ||
        scheduleQuery.error?.message ||
        'An unknown error occurred.';
      handleErrorDialogOpen(errorText);
    }
  }, [
    scheduleQuery.isError,
    scheduleQuery.error,
    hasEmployeesError,
    employeesError,
    handleErrorDialogOpen,
  ]);

  useEffect(() => {
    if (!shouldUseAnyAvailableFlow || bookableEligibleEmployees.length === 0) {
      setIsLoadingAnyAvailableSchedules(false);
      return;
    }

    let isCancelled = false;

    const loadSchedules = async () => {
      setIsLoadingAnyAvailableSchedules(true);

      try {
        const scheduleEntries = await Promise.all(
          bookableEligibleEmployees.map(async (employee) => {
            const schedule = await ensureEmployeeSchedule(employee.id);
            return [employee.id, schedule] as const;
          })
        );

        if (isCancelled) {
          return;
        }

        setAnyAvailableLoadFailed(false);
        setAnyAvailableSchedules((prev) => {
          const next = { ...prev };
          scheduleEntries.forEach(([employeeId, schedule]) => {
            next[employeeId] = schedule;
          });
          return next;
        });
      } catch (error) {
        if (!isCancelled) {
          setAnyAvailableLoadFailed(true);
          const message = error instanceof Error ? error.message : 'Failed to load staff availability.';
          handleErrorDialogOpen(message);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingAnyAvailableSchedules(false);
        }
      }
    };

    loadSchedules();

    return () => {
      isCancelled = true;
    };
  }, [bookableEligibleEmployees, ensureEmployeeSchedule, handleErrorDialogOpen, shouldUseAnyAvailableFlow]);

  useEffect(() => {
    if (!isAnyAvailableSelected || !requiresCardCompatibleEmployee || anyAvailableCandidateEmployees.length === 0) {
      setIsLoadingAnyAvailableCardEligibility(false);
      return;
    }

    let isCancelled = false;

    const loadCardEligibility = async () => {
      setIsLoadingAnyAvailableCardEligibility(true);

      try {
        const entries = await Promise.all(
          anyAvailableCandidateEmployees.map(async (employee) => {
            const canTakeCard = await ensureCardEligibility(employee.id);
            return [employee.id, canTakeCard] as const;
          })
        );

        if (isCancelled) {
          return;
        }

        setAnyAvailableCardEligibility((prev) => {
          const next = { ...prev };
          entries.forEach(([employeeId, canTakeCard]) => {
            next[employeeId] = canTakeCard;
          });
          return next;
        });
      } catch (error) {
        if (!isCancelled) {
          const message = error instanceof Error ? error.message : 'Failed to load payment availability.';
          handleErrorDialogOpen(message);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingAnyAvailableCardEligibility(false);
        }
      }
    };

    loadCardEligibility();

    return () => {
      isCancelled = true;
    };
  }, [
    anyAvailableCandidateEmployees,
    ensureCardEligibility,
    handleErrorDialogOpen,
    isAnyAvailableSelected,
    requiresCardCompatibleEmployee,
  ]);

  const isAnyAvailableBlockedByPaymentCompatibility = useMemo(() => {
    if (!isAnyAvailableSelected || !requiresCardCompatibleEmployee) {
      return false;
    }

    return eligibleEmployees.length > 0 && bookableEligibleEmployees.length === 0;
  }, [bookableEligibleEmployees.length, eligibleEmployees.length, isAnyAvailableSelected, requiresCardCompatibleEmployee]);

  const anyAvailableSlotsByDate = useMemo(() => {
    if (!shouldUseAnyAvailableFlow || !appointment?.appointmentDuration || bookableEligibleEmployees.length === 0) {
      return null;
    }

    const scheduleMaps = bookableEligibleEmployees
      .map((employee) => anyAvailableSchedules[employee.id])
      .filter((scheduleMap): scheduleMap is Map<string, string> => !!scheduleMap);

    if (scheduleMaps.length === 0) {
      return null;
    }

    return getUnionSlotsForSchedules(scheduleMaps, appointment.appointmentDuration);
  }, [anyAvailableSchedules, appointment?.appointmentDuration, bookableEligibleEmployees, shouldUseAnyAvailableFlow]);

  // Null while availability is still on its way, so the calendar never opens
  // empty and then fills in (it showed "no times" for a moment, then jumped).
  const isWaitingForAnyAvailable =
    anyAvailableSlotsByDate === null &&
    !anyAvailableLoadFailed &&
    !!appointment?.appointmentDuration &&
    bookableEligibleEmployees.some((employee) => !anyAvailableSchedules[employee.id]);
  const scheduleCacheForSelection = shouldUseAnyAvailableFlow
    ? (isWaitingForAnyAvailable ? null : buildAnyAvailableSchedulePlaceholder(anyAvailableSlotsByDate))
    : scheduleQuery.data;

  // The full-dialog spinner is only for data the current step can't show
  // without. Background refreshes keep the step on screen: replacing the whole
  // dialog with a spinner for a moment made it flash on every click.
  const needsAvailability = currentStep >= 2;
  const isLoading = (
    employeesLoading ||
    businessConfigQuery.isLoading ||
    (needsAvailability && scheduleQuery.isLoading) ||
    cardEligibilityQuery.isLoading ||
    (needsAvailability && shouldUseAnyAvailableFlow && bookableEligibleEmployees.length > 0 &&
      isLoadingAnyAvailableSchedules && anyAvailableSlotsByDate === null) ||
    (isAnyAvailableSelected && requiresCardCompatibleEmployee && anyAvailableCandidateEmployees.length > 0 && isLoadingAnyAvailableCardEligibility) ||
    !imagesLoaded ||
    isSubmitting
  );

  const handleScheduleCacheUpdate = useCallback(({
    employeeId,
    appointmentDate,
    appointmentTime,
    appointmentDuration,
    cancel = false,
  }: ScheduleCacheUpdateInput) => {
    if (!employeeId || !appointmentDate || !appointmentTime || !appointmentDuration) {
      return;
    }

    applyScheduleBitstringPatch(
      queryClient,
      tenantId,
      employeeId,
      appointmentDate,
      appointmentTime,
      appointmentDuration,
      cancel
    );
  }, [queryClient, tenantId]);

  useEffect(() => {
    if (hasManualStepOverride) {
      return;
    }

    if (isOrchestratorMode || shouldHideEmployeePicker) {
      setIsAnyAvailableSelected(true);
      if (!appointment?.employeeID) {
        setSelectedEmployee(null);
      }
      if (currentStep === 0) {
        setCurrentStep(1);
      }
      return;
    }

    if (preferredEmployeeId) {
      if (selectedEmployee?.id === preferredEmployeeId) return;
      const preferred = availableEmployees.find((employee) => employee.id === preferredEmployeeId);
      if (preferred) {
        setIsAnyAvailableSelected(false);
        setSelectedEmployee(preferred);
        setSelectedServicesList(null);
        if (currentStep === 0) {
          setCurrentStep(1);
        }
      }
      return;
    }

    if (availableEmployees.length === 1 && !selectedEmployee && !isAnyAvailableSelected) {
      setIsAnyAvailableSelected(false);
      setSelectedEmployee(availableEmployees[0]);
      setSelectedServicesList(null);
      if (currentStep === 0) {
        setCurrentStep(1);
      }
    }
  }, [availableEmployees, selectedEmployee, preferredEmployeeId, currentStep, hasManualStepOverride, isAnyAvailableSelected, isOrchestratorMode, shouldHideEmployeePicker, appointment?.employeeID]);

  useEffect(() => {
    if (imagesLoaded) {
      return;
    }

    if (employeesLoading) {
      return;
    }

    if (!availableEmployees.length) {
      setProfilePictures({});
      setImagesLoaded(true);
      return;
    }

    let isCancelled = false;

    const loadImages = async () => {
      try {
        const images = await fetchAndPreloadImages(availableEmployees);
        if (!isCancelled) {
          setProfilePictures(images);
          setImagesLoaded(true);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to preload employee images', error);
          setImagesLoaded(true);
        }
      }
    };

    loadImages();

    return () => {
      isCancelled = true;
    };
  }, [availableEmployees, imagesLoaded, employeesLoading]);

  useEffect(() => {
    if (!appointment) {
      setSelectedServicesList(null);
      setAvailableSlots([]);
      setHasManualStepOverride(false);
      setIsAnyAvailableSelected(isOrchestratorMode || shouldHideEmployeePicker);

      if (preferredEmployeeId) {
        const preferredCandidates = variant === 'customer' ? availableEmployees : employeesData;
        const preferred = preferredCandidates.find((employee) => employee.id === preferredEmployeeId) ?? null;
        setSelectedEmployee(preferred);
        setCurrentStep(preferred ? 1 : minimumSelectableStep);
        return;
      }

      setSelectedEmployee(null);
      setCurrentStep(minimumSelectableStep);
      return;
    }

    // Avoid clearing a valid prefilled employee while employee data is still loading.
    if (!useEmployeesOverride && employeesQuery.data === undefined) {
      return;
    }

    if (!appointment.employeeID) {
      const validServices = getServicesByIds(appointment.servicesList, services);
      const selectedEmployeeStillAvailable = selectedEmployee
        ? availableEmployees.some((employee) => employee.id === selectedEmployee.id)
        : false;
      const shouldUseAnyAvailable =
        allowAnyAvailable &&
        (isOrchestratorMode || shouldHideEmployeePicker || isAnyAvailableSelected || !selectedEmployeeStillAvailable);
      const preferredEmployee = preferredEmployeeId
        ? availableEmployees.find((employee) => employee.id === preferredEmployeeId) ?? null
        : null;
      const employeesToEvaluate = shouldUseAnyAvailable
        ? availableEmployees
        : preferredEmployee
          ? [preferredEmployee]
          : selectedEmployeeStillAvailable && selectedEmployee
            ? [selectedEmployee]
            : availableEmployees;
      const matchingEligibleEmployees = getEligibleEmployeesForServices(validServices, employeesToEvaluate);
      const hasCompatibleServiceSelection = validServices.length === 0 || matchingEligibleEmployees.length > 0;

      setIsAnyAvailableSelected(shouldUseAnyAvailable);
      if (!hasCompatibleServiceSelection) {
        setSelectedServicesList(null);
        setAvailableSlots([]);
        if (appointment.servicesList.length > 0 || appointment.appointmentDate || appointment.appointmentTime) {
          handleAppointment({
            ...appointment,
            billingAmount: 0,
            appointmentDate: '',
            appointmentDuration: 0,
            appointmentTime: '',
            appointmentTitle: '',
            servicesList: [],
          });
          handleErrorDialogOpen('The selected services cannot be booked with the same staff member. Please choose another combination.');
        }
      } else if (validServices.length > 0) {
        setSelectedServicesList(validServices);
      } else {
        setSelectedServicesList(null);
      }

      setSelectedEmployee(
        shouldUseAnyAvailable || isOrchestratorMode || shouldHideEmployeePicker
          ? null
          : preferredEmployee ?? (selectedEmployeeStillAvailable ? selectedEmployee : null)
      );
      if (!hasManualStepOverride) {
        const targetStep = appointment.appointmentTime
          ? 3
          : appointment.appointmentDate
            ? 2
            : 1;
        setCurrentStep((prev) => Math.max(prev, targetStep));
      }
      return;
    }

    const employee = availableEmployees.find((e) => e.id === appointment.employeeID);
    if (!employee) {
      handleErrorDialogOpen('Your previously picked service provider is not available anymore. Please choose another.');
      handleAppointment(null);
      return;
    }

    const shouldPreserveAnyAvailableSelection =
      (isOrchestratorMode || shouldHideEmployeePicker || (
        variant === 'customer' &&
        isAnyAvailableSelected &&
        !!appointment.servicesList?.length
      ));

    setIsAnyAvailableSelected(shouldPreserveAnyAvailableSelection);
    setSelectedEmployee(employee);
    if (currentStep === 0 && !hasManualStepOverride) {
      setCurrentStep(1);
    }

    if (appointment.servicesList?.length) {
      const validServices: Service[] = appointment.servicesList
        .map((serviceRecord) => services.find((service) => service.id === serviceRecord.id))
        .filter((service): service is Service => !!service && service.employees?.includes(employee.id));
      setSelectedServicesList(validServices.length > 0 ? validServices : null);
    } else {
      setSelectedServicesList(null);
    }
  }, [allowAnyAvailable, appointment, availableEmployees, employeesData, services, handleAppointment, currentStep, hasManualStepOverride, handleErrorDialogOpen, isAnyAvailableSelected, preferredEmployeeId, selectedEmployee, variant, minimumSelectableStep, isOrchestratorMode, shouldHideEmployeePicker, useEmployeesOverride, employeesQuery.data]);

  const resolveEmployeeForAnyAvailableSlot = useCallback((slot: string, scheduleOverrides?: Record<string, Map<string, string>>) => {
    if (!appointment?.appointmentDate || !appointment.appointmentDuration) {
      return null;
    }

    const matchingEmployees = bookableEligibleEmployees.filter((employee) => {
      const scheduleMap =
        scheduleOverrides?.[employee.id] ??
        anyAvailableSchedules[employee.id] ??
        queryClient.getQueryData<Map<string, string>>(['employeeSchedule', tenantId, employee.id]);
      const bitString = scheduleMap?.get(appointment.appointmentDate);
      if (!bitString) {
        return false;
      }

      return isTimeFree(bitString, timeToMinutes(slot), appointment.appointmentDuration);
    });

    if (!matchingEmployees.length) {
      return null;
    }

    const randomIndex = Math.floor(Math.random() * matchingEmployees.length);
    return matchingEmployees[randomIndex] ?? null;
  }, [anyAvailableSchedules, appointment, bookableEligibleEmployees, queryClient, tenantId]);

  const buildAppointmentWithEmployee = useCallback((
    baseAppointment: Appointment,
    employee: Employee,
    overrides?: Partial<Appointment>,
  ): Appointment => ({
    ...baseAppointment,
    ...overrides,
    employeeID: employee.id,
    employeeName: getEmployeeLabel(employee),
    employeeEmail: employee.email || '',
    employeePhoneNumber: employee.phone || '',
  }), []);

  const applyEmployeeSelection = useCallback((
    employee: Employee,
    baseAppointment: Appointment,
    overrides?: Partial<Appointment>,
  ) => {
    setSelectedEmployee(employee);
    handleAppointment(buildAppointmentWithEmployee(baseAppointment, employee, overrides));
  }, [buildAppointmentWithEmployee, handleAppointment]);

  const resetAfterAnyAvailableConflict = useCallback((
    baseAppointment: Appointment,
    message: string,
    options?: { clearEmployee?: boolean; refreshSchedules?: boolean },
  ) => {
    const shouldClearEmployee = options?.clearEmployee ?? false;

    handleAppointment(
      shouldClearEmployee
        ? {
          ...baseAppointment,
          appointmentTime: '',
          employeeID: '',
          employeeName: '',
          employeeEmail: '',
          employeePhoneNumber: '',
        }
        : { ...baseAppointment, appointmentTime: '' }
    );

    if (shouldClearEmployee) {
      setSelectedEmployee(null);
    }

    const selectedDate = baseAppointment.appointmentDate;
    const selectedSlot = baseAppointment.appointmentTime;
    const getRemainingSlotsForDate = (slotsByDate?: Map<string, string[] | undefined> | null) => {
      if (!selectedDate || !slotsByDate) {
        return [];
      }

      return (slotsByDate.get(selectedDate) ?? []).filter((slot) => slot !== selectedSlot);
    };
    const applySlotRecoveryState = (slots: string[]) => {
      setAvailableSlots(slots);
      setCurrentStep(slots.length > 0 ? 3 : 2);
    };

    applySlotRecoveryState(getRemainingSlotsForDate(anyAvailableSlotsByDate));
    setHasManualStepOverride(false);

    if (options?.refreshSchedules) {
      void refreshAnyAvailableSchedules()
        .then((latestSchedules) => {
          if (!selectedDate || !baseAppointment.appointmentDuration) {
            return;
          }

          const scheduleMaps = bookableEligibleEmployees
            .map((employee) => latestSchedules[employee.id])
            .filter((scheduleMap): scheduleMap is Map<string, string> => !!scheduleMap);

          if (scheduleMaps.length === 0) {
            applySlotRecoveryState([]);
            return;
          }

          const refreshedSlotsByDate = getUnionSlotsForSchedules(
            scheduleMaps,
            baseAppointment.appointmentDuration
          );
          applySlotRecoveryState(getRemainingSlotsForDate(refreshedSlotsByDate));
        })
        .catch((error) => {
          console.error('Failed to refresh any-available schedules', error);
        });
    }

    handleErrorDialogOpen(message);
  }, [anyAvailableSlotsByDate, bookableEligibleEmployees, handleAppointment, handleErrorDialogOpen, refreshAnyAvailableSchedules]);

  useEffect(() => {
    if (variant === 'internal') {
      return;
    }
    if (!selectedEmployee || availableEmployees.length === 0 || bookingConfirmation) {
      return;
    }
    if (!availableEmployees.some((employee) => employee.id === selectedEmployee.id)) {
      if (
        isAnyAvailableSelected &&
        appointment?.appointmentDate &&
        appointment.appointmentTime &&
        appointment.appointmentDuration
      ) {
        const fallbackEmployee = resolveEmployeeForAnyAvailableSlot(appointment.appointmentTime);

        if (fallbackEmployee) {
          applyEmployeeSelection(fallbackEmployee, appointment);
          return;
        }

        resetAfterAnyAvailableConflict(appointment, 'That time was just taken. Please choose another slot.', {
          clearEmployee: true,
        });
        return;
      }

      setSelectedEmployee(null);
      setIsAnyAvailableSelected(isOrchestratorMode);
      setSelectedServicesList(null);
      setCurrentStep(minimumSelectableStep);
    }
  }, [
    appointment,
    applyEmployeeSelection,
    availableEmployees,
    bookingConfirmation,
    isAnyAvailableSelected,
    resetAfterAnyAvailableConflict,
    resolveEmployeeForAnyAvailableSlot,
    selectedEmployee,
    variant,
    minimumSelectableStep,
    isOrchestratorMode,
  ]);

  const handleAnyAvailableSlotSelect = useCallback((slot: string) => {
    if (!shouldUseAnyAvailableFlow || !appointment) {
      return true;
    }

    const resolvedEmployee = resolveEmployeeForAnyAvailableSlot(slot);
    if (!resolvedEmployee) {
      resetAfterAnyAvailableConflict(appointment, 'That time was just taken. Please choose another slot.', {
        refreshSchedules: true,
      });
      return false;
    }

    applyEmployeeSelection(resolvedEmployee, appointment, { appointmentTime: slot });
    return true;
  }, [appointment, applyEmployeeSelection, resetAfterAnyAvailableConflict, resolveEmployeeForAnyAvailableSlot, shouldUseAnyAvailableFlow]);

  const handleAnyAvailableConflictResolution = useCallback(async () => {
    if (!shouldUseAnyAvailableFlow || !appointment?.appointmentTime) {
      return null;
    }

    const latestSchedules = await refreshAnyAvailableSchedules();
    const fallbackEmployee = resolveEmployeeForAnyAvailableSlot(appointment.appointmentTime, latestSchedules);

    if (!fallbackEmployee) {
      return null;
    }

    applyEmployeeSelection(fallbackEmployee, appointment);

    return fallbackEmployee;
  }, [appointment, applyEmployeeSelection, refreshAnyAvailableSchedules, resolveEmployeeForAnyAvailableSlot, shouldUseAnyAvailableFlow]);

  const handleNextStep = useCallback(() => {
    if (variant === 'internal' && currentStep === 1 && appointment?.appointmentDate && appointment?.appointmentTime && appointment?.appointmentDuration && scheduleQuery.data) {
      const parsedDate = dayjs(appointment.appointmentDate, 'MMDDYYYY', true);
      if (!parsedDate.isValid()) {
        handleAppointment({ ...appointment, appointmentDate: '', appointmentTime: '' });
        setAvailableSlots([]);
        setCurrentStep(2);
        handleErrorDialogOpen('Your selected date/time is not available anymore. Please pick a new time.');
        return;
      }
      const now = dayjs.tz();
      const isRetroAppointment = parsedDate.endOf('day').isBefore(now);

      const bitString = scheduleQuery.data.get(appointment.appointmentDate);
      if (!bitString) {
        setAvailableSlots([]);
        setCurrentStep(2);
        handleErrorDialogOpen('Your selected date/time is not available anymore. Please pick a new time.');
        return;
      }

      const disablePastTimes = !isRetroAppointment && parsedDate.isSame(now, 'day');
      const freeSlots = findFreeSlots(bitString, appointment.appointmentDuration, disablePastTimes);
      const earliestBookingDateTime = variant === 'internal' ? null : getEarliestBookingDateTime();
      const validSlots = filterSlotsByMinimumNoticeThreshold(freeSlots, parsedDate, earliestBookingDateTime);
      setAvailableSlots(validSlots);

      const slotIsFree = isTimeFree(bitString, timeToMinutes(appointment.appointmentTime), appointment.appointmentDuration);
      const slotMatchesCustomerOptions = validSlots.includes(appointment.appointmentTime);

      const isAppointmentTimeTooSoon =
        variant !== 'internal' &&
        !isRetroAppointment &&
        normalizedMinimumBookingNoticeHours > 0 &&
        !!earliestBookingDateTime &&
        freeSlots.includes(appointment.appointmentTime) &&
        !validSlots.includes(appointment.appointmentTime);

      if (slotIsFree && (slotMatchesCustomerOptions || variant === 'internal') && !isAppointmentTimeTooSoon) {
        setCurrentStep(4);
        return;
      }

      handleAppointment({ ...appointment, appointmentTime: '' });
      setCurrentStep(2);
      handleErrorDialogOpen('Your selected service(s) can not be booked during this time. Please pick a new time.');
      return;
    }

    if (
      currentStep === 3 &&
      isAnyAvailableSelected &&
      appointment?.appointmentDate &&
      appointment.appointmentTime &&
      !selectedEmployee
    ) {
      const resolvedEmployee = resolveEmployeeForAnyAvailableSlot(appointment.appointmentTime);

      if (!resolvedEmployee) {
        resetAfterAnyAvailableConflict(appointment, 'That time was just taken. Please choose another slot.');
        return;
      }

      applyEmployeeSelection(resolvedEmployee, appointment);
    }

    goToNextStep();
  }, [
    appointment,
    applyEmployeeSelection,
    currentStep,
    getEarliestBookingDateTime,
    goToNextStep,
    handleAppointment,
    handleErrorDialogOpen,
    isAnyAvailableSelected,
    normalizedMinimumBookingNoticeHours,
    resetAfterAnyAvailableConflict,
    resolveEmployeeForAnyAvailableSlot,
    scheduleQuery.data,
    selectedEmployee,
    variant,
  ]);

  const isNextDisabled =
    (currentStep === 0 && !selectedEmployee && !isAnyAvailableSelected) ||
    (currentStep === 1 && (!selectedServicesList || (isAnyAvailableSelected && bookableEligibleEmployees.length === 0))) ||
    (currentStep === 2 && availableSlots.length <= 0) ||
    (currentStep === 3 && (!appointment?.appointmentDate || !appointment.appointmentTime));

  return {
    allowAnyAvailable,
    allEmployees: employeesData,
    anyAvailableSlotsByDate,
    appointment,
    attemptedCardPayment,
    availableEmployees,
    availableSlots,
    bookingConfirmation,
    bookableEligibleEmployees,
    businessConfig: businessConfigQuery.data,
    canTakeCard: !!cardEligibilityQuery.data || false,
      currentStep,
    errorMessage,
    bookingUxConfig,
    shouldHideEmployeePicker,
    isResourceMode,
    isOrchestratorMode,
    goToNextStep,
    goToPreviousStep,
    handleAnyAvailableConflictResolution,
    handleAnyAvailableSlotSelect,
    handleBookingConfirmation,
    handleErrorDialogClose,
    handleErrorDialogExited,
    handleErrorDialogOpen,
    handleNextStep,
    handleScheduleCacheUpdate,
    imagesLoaded,
    isAnyAvailableSelected,
    isAnyAvailableBlockedByPaymentCompatibility,
    isLoading,
    isNextDisabled,
    openErrorDialog,
    paid,
    profilePictures,
    scheduleCacheForSelection,
    selectedEmployee,
    selectedServicesList,
    selectionSummary,
    setAvailableSlots,
    setIsAnyAvailableSelected,
    setIsSubmitting,
    setSelectedEmployee,
    setSelectedServicesList,
    shouldClearAfterSuccess,
    steps,
    normalizedMinimumBookingNoticeHours,
  };
};
